import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Bienvenido a la sala de redacción");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative z-10">
      <div className="hidden lg:block relative border-r border-border">
        <img
          src="https://images.pexels.com/photos/518543/pexels-photo-518543.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Periódicos"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#02203F]/85" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3" data-testid="app-logo">
            <LogoMark className="h-10 w-10" navy="#FFFFFF" />
            <div>
              <div className="serif text-3xl font-bold leading-none">Redactia</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#52DDCF] mt-1">
                Agencia Multimedios SRL
              </div>
            </div>
          </div>
          <div>
            <h1 className="text-5xl font-bold leading-[1.05] tracking-tight">
              Rigor periodístico,<br />velocidad digital.
            </h1>
            <p className="text-sm text-white/70 mt-6 max-w-md leading-relaxed">
              Ingesta de notas de prensa, redacción asistida por IA, optimización SEO y
              distribución simultánea a PaginaUno.Do, LaSeguridadSocial.Do, redes sociales,
              WhatsApp y Telegram.
            </p>
          </div>
          <div className="mono text-xs text-white/40">Sociedad de la Información · República Dominicana</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="kicker text-primary">Acceso interno</div>
          <h2 className="text-3xl font-bold tracking-tight mt-1 mb-8">Iniciar sesión</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Correo</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usted@multimedios.do"
                className="mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">Contraseña</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive mt-4" data-testid="login-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full mt-6 h-11"
            data-testid="login-submit-button"
          >
            {loading ? "Verificando…" : "Entrar"}
          </Button>

          <p className="text-xs text-muted-foreground mt-8 leading-relaxed">
            Acceso restringido al personal autorizado. Toda actividad queda registrada conforme a
            nuestras políticas de seguridad y protección de datos.
          </p>
        </form>
      </div>
    </div>
  );
}
